import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Button, Form, FormInstance, Input } from "antd";
import React, { Fragment, useEffect, useRef, memo } from "react";
import VariationOptionForm from "./SKUModalForm";

import type { UploadFile } from "antd/es/upload/interface";


interface ProductManyPriceChoiceProps {
  form: FormInstance;
  skuItems: VariationData[];
  variants: VariantsInput;
  skuImage: UploadFile[] | undefined;
  setSkuImage: (skuImage: UploadFile[] | undefined) => void;
  editMode?: boolean;
  copyMode?: boolean;
  isDraftSave?: boolean;
  editData?: { variant: Variant, price: Price, index: number } | null;
  originalPrices?: PricesInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้สำหรับ restore c1-c3 (optional)
  originalVariants?: VariantsInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้ restore shipping_method + dimensions (optional)
  speedHasSearched: boolean;
  setSpeedHasSearched: (speedHasSearched: boolean) => void;
  shouldBlinkButton: boolean; // ✅ สำหรับควบคุมการกระพริบของปุ่มค้นหาบริษัทขนส่ง
  setShouldBlinkButton: (shouldBlinkButton: boolean) => void; // ✅ สำหรับควบคุมการกระพริบของปุ่มค้นหาบริษัทขนส่ง
  factoryId?: string; // ✅ รับ factoryId
  percentCommission?: number; // ✅ percent_commission จากประเภทสินค้าที่เลือก
  categoryName?: string; // ✅ ชื่อประเภทสินค้าที่เลือก
}

// ✅ rerender-memo: Wrap component ด้วย memo เพื่อลด re-render ที่ไม่จำเป็น
const SKUMainCard: React.FC<ProductManyPriceChoiceProps> = memo(({
  form,
  skuItems,
  variants,
  skuImage,
  setSkuImage,
  editMode = false,
  copyMode = false,
  isDraftSave = false,
  editData = null,
  originalPrices = [],
  originalVariants = [],
  speedHasSearched,
  setSpeedHasSearched,
  shouldBlinkButton,
  setShouldBlinkButton,
  factoryId,
  percentCommission,
  categoryName,
}) => {

  const addRef = useRef<(() => void) | null>(null);
  //ตรวจสอบว่ามี sku_items หรือไม่ ถ้าไม่มีให้เพิ่มตัวเลือกแรก
  // ใช้ useRef เพื่อเก็บฟังก์ชัน add ที่จะถูกเรียกใน useEffect
  // เพื่อให้แน่ใจว่าเราสามารถเรียกใช้ฟังก์ชันนี้ได้เมื่อ component ถูก mount
  // และเมื่อ form ถูกเปลี่ยนแปลง
  useEffect(() => {
    // ✅ สำคัญ: ห้าม auto-add ใน edit/copy mode
    // เพราะจะสร้างแถวเปล่าที่มี initialValue (เช่น shipping_method="self")
    // แล้วไปทับค่าที่เราตั้งจาก editData/copyData ทำให้เห็นข้อมูลขนาด/ขนส่งหาย
    if (editMode || copyMode) return;

    if (addRef.current) {
      const currentSkuItems = form.getFieldValue("sku_items");
      if (!currentSkuItems || currentSkuItems.length === 0) {
        // เพิ่มตัวเลือกแรกโดยอัตโนมัติ (เฉพาะ create mode)
        addRef.current();
      }
    }
  }, [form, skuItems, editMode, copyMode]); // เพิ่ม mode เพื่อคุม auto-add

  return (
    <FormWrapper>
      <Form.List name="sku_items">
        {(fields, { add, remove }) => {
          addRef.current = add;

          return (<>
            {fields.map(({ key, name, ...restField }, index) => (
              <Fragment key={key}>
                <WrapperForm>
                  <Form.Item
                    {...restField}
                    name={[name, "name"]}
                    rules={[
                      {
                        required: true,
                        message: "กรุณากรอกชื่อตัวเลือก",
                      },
                    ]}
                    label="ชื่อตัวเลือก"
                    className="form-varation-name"
                    initialValue=""
                    hidden={true}
                  >
                    <Input
                      style={{ marginLeft: 20, width: 480 }}
                      placeholder="ชื่อตัวเลือก"
                      className="form-varation-name__input"
                      size="small"
                    //readOnly
                    />
                  </Form.Item>
                  <WrapperButton>
                    <DeleteOutlined
                      style={{ marginLeft: 0, fontSize: 16 }}
                      onClick={() => remove(name)}
                    />
                  </WrapperButton>
                </WrapperForm>
                <VariationOpteionFormWrapper>
                  <VariationOptionForm
                    name={name}
                    form={form}
                    skuItem={skuItems[name]?.items}
                    variants={variants}
                    skuImage={skuImage}
                    setSkuImage={setSkuImage}
                    editMode={editMode}
                    copyMode={copyMode}
                    isDraftSave={isDraftSave}
                    editData={editData}
                    originalPrices={originalPrices}
                    originalVariants={originalVariants}
                    speedHasSearched={speedHasSearched}
                    setSpeedHasSearched={setSpeedHasSearched}
                    shouldBlinkButton={shouldBlinkButton}
                    setShouldBlinkButton={setShouldBlinkButton}
                    factoryId={factoryId}
                    percentCommission={percentCommission}
                    categoryName={categoryName}
                  />
                </VariationOpteionFormWrapper>
              </Fragment>
            ))}
            <ButtonWrapper>
              <Form.Item>
                <Button
                  // สร้างแค่ 1 ตัวเลือก
                  disabled={fields.length >= 1}
                  hidden={fields.length >= 1}//ซ่อนปุ่มเพิ่มถ้ามีตัวเลือกแล้ว
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  style={{
                    width: 300,
                  }}
                  onClick={() => add()}
                >
                  เพิ่ม
                </Button>
              </Form.Item>
            </ButtonWrapper>
          </>
          );
        }
        }
      </Form.List>
    </FormWrapper>
  );
});

SKUMainCard.displayName = "SKUMainCard";

export default SKUMainCard;

const VariationOpteionFormWrapper = styled.div``;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const WrapperForm = styled.div`
display: flex;
flex-direction: row;
justify-content: space-between;
width: 100%;
align-items: center;


`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -20px;
`;
